import React, { ReactElement, useState, useEffect, useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import styled from '@emotion/styled';
import MasterConfig from '../../config/Master';
import UsernameInputV2 from '../../atoms/UsernameInputV2';
import { useJoin } from '../../contexts/JoinContext';
import Autocomplete from '../../atoms/AutocompleteMain';
import {
  computeAge,
  enableOneTapGoogleLogin,
  isQAautomation,
  isQAautomationEmail,
  lazilyEvaluateVal,
  termsOfUseUrl,
} from '../../utils/helpers';
import JoinActionButton from './JoinActionButton';
import { DashlineSeparator } from './styledElements';
import { extractLocationData, geocodeByPlaceId, getLocalItem } from '../../common';
import { VYSION_ACTIONS } from '../../utils/vysion';
import { handleJoinVysionEvent } from '../../utils/vysion/join';
import { JoinOnFocusVysionEventsEnum } from '../../utils/vysion/join/onFocus';
import { EVENT_NAMES, interestedInForMixpanel } from '../../constants/mixpanel';
import TextLink from '../common/TextLink';
import Hcaptcha from '../auth/Hcaptcha';
import { JoinClickVysionEventsEnum } from '../../utils/vysion/join/click';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import breakpoints from '../../theme/material/breakpoints';
import { mixpanelTrackEvent } from '../../utils/mixpanel/common';
import { passwordlessFeatureName, passwordlessVariant } from '../../utils/evidentlyFeatures/passwordlessEvaluation';
import useEvidently from '../../hooks/useEvidently';
import colors from '../../theme/colors';

const Form = styled('form')({});

const ButtonContainer = styled('div')({
  textAlign: 'center',
  paddingTop: 10,
});

const BottomMsg = styled('div')({
  textAlign: 'center',
  margin: '22px auto',
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '-0.5px',
  marginBottom: 6,
  color: '#8F8F8F',
  maxWidth: 250,
  [breakpoints.down('sm')]: {
    marginBottom: 0,
  },
});

const HCaptchaContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const JoinCreateAccountV2 = (): ReactElement => {
  const { isMobile } = useMediaBreakpoints();
  const {
    currentFormData,
    isEuUser,
    attributes,
    isSubmitFailed,
    setCurrentFormData,
    nextStep,
    convertToFormData,
    onFormSubmitted,
  } = useJoin();
  const [locationBlurred, setLocationBlurred] = useState(false);
  const [locationSelected, setLocationSelected] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState('');
  const [selectedAddress, setSelectedAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const locationIsEmpty = locationBlurred && !currentFormData.address;
  const locationNotSelected = locationBlurred && !locationSelected;
  const invalidLocation = locationIsEmpty || locationNotSelected;

  const { useActiveVariation } = useEvidently();
  const joinPasswordlessVariant = useActiveVariation(passwordlessFeatureName);
  const isPasswordless = joinPasswordlessVariant === passwordlessVariant;

  const { cf_country, isJoinCaptchaEnabled } = attributes;

  const joinPreData = JSON.parse(getLocalItem('join_predata'));

  const underAgeCheck = ({ month, day, year }) => {
    const ageYear = computeAge(`${year}/${month}/${day}`);

    return typeof ageYear === 'number' && ageYear < 18;
  };

  useEffect(() => {
    if (isSubmitFailed) {
      setCurrentFormData({ 'h-captcha-response': '' });
      setIsLoading(false);
    }
  }, [isSubmitFailed]);

  const allowSubmit = useMemo(
    () =>
      Boolean(
        !underAgeCheck(currentFormData) &&
          currentFormData.username &&
          currentFormData.agreements &&
          currentFormData.address &&
          currentFormData.country &&
          currentFormData.city &&
          currentFormData.latitude &&
          currentFormData.longitude &&
          currentFormData.username.match(/^[a-zA-Z-]+$/) &&
          /^[A-Za-z][A-Za-z-]*$/.test(currentFormData.username) &&
          currentFormData.username.length >= 3 &&
          currentFormData.username.length <= 16 &&
          (isQAautomation() ||
            isQAautomationEmail(joinPreData?.email ?? currentFormData?.email ?? '') ||
            !isJoinCaptchaEnabled ||
            currentFormData['h-captcha-response'])
      ),
    [currentFormData]
  );

  const onLocationChange = value => {
    setCurrentFormData({ address: value });
  };

  const handleLocationChange = e => {
    onLocationChange(e);
    setLocationBlurred(false);
    setLocationSelected(false);
  };

  const handleLocationBlur = () => {
    // falls back to previous selected location
    if (selectedPlaceId === currentFormData.placeId && selectedPlaceId !== '') {
      handleSelectLocation(selectedAddress, selectedPlaceId, currentFormData?.NEWLOCATION);
    } else {
      setLocationBlurred(true);
    }
  };

  const setSelectedLocationId = useCallback(() => {
    // this stores previous selected location to prevent onBlur errors shown
    setSelectedPlaceId(currentFormData.placeId);
    setSelectedAddress(currentFormData.address);
  }, [currentFormData.address, currentFormData.placeId]);

  const handleSelectLocation = (myAddress, myPlaceId, details = null) => {
    if (!locationSelected) setLocationSelected(true);

    // Immediately update the selected state to prevent blur conflicts
    setSelectedPlaceId(myPlaceId);
    setSelectedAddress(myAddress);

    locationSelectHandler(myAddress, myPlaceId, details);
  };

  const locationSelectHandler = (address, placeId, details = null) => {
    if (MasterConfig.tomtomEnabled && details !== null) {
      const locationData = extractLocationData(details);
      const newLocData = {
        NEWLOCATION: details,
        address,
        ...locationData,
      };

      setCurrentFormData({ ...newLocData });
    } else {
      geocodeByPlaceId(placeId)
        .then(results => {
          const loc = results[0];

          if (loc.geometry) {
            const locationData = extractLocationData(loc);
            const newLocData = {
              NEWLOCATION: loc,
              address,
              ...locationData,
            };

            setCurrentFormData({ ...newLocData });
          }
        })
        .catch(error => {
          Sentry.captureException(error, { tags: { component: 'JoinCreateAccountV2', action: 'Location' } });
        });
    }
  };

  const submit = (trackMixpanelCBAction: (hashId: string, isAccountIsMixpanelEligableEnabled?: boolean) => void) => {
    const formData = convertToFormData();

    onFormSubmitted(formData, true, trackMixpanelCBAction);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    handleJoinVysionEvent({
      action: VYSION_ACTIONS.CLICK,
      event: JoinClickVysionEventsEnum.CLICK_JOIN_CREATE_ACCOUNT,
    });

    submit(hashId => {
      if (hashId) {
        setIsLoading(false);

        const userData = { hash_id: hashId, ...currentFormData };

        mixpanelTrackEvent({
          eventName: EVENT_NAMES.SECOND_STEP_COMPLETED,
          payload: {
            userProperties: {
              Gender: userData.sex === 1 ? 'man' : 'woman',
              'Want To': userData.account_type === 1 ? 'make offers' : 'receive offers',
              'Dating Intentions': interestedInForMixpanel(userData),
              DOB: `${userData.day}/${userData.month}/${userData.year}`,
              'Profile City': userData.city,
              'Profile Country': userData.country,
              'Display Name': userData.username,
              $name: userData.username,
            },
          },
        });

        nextStep();
        enableOneTapGoogleLogin(false);
      }
    });
  };

  // validatons
  const addressValidationStatus = () => {
    return lazilyEvaluateVal(status, 2, false, 'address', 'status');
  };

  const addressValidationMsg = () => {
    return lazilyEvaluateVal(status, 2, false, 'address', 'message');
  };

  useEffect(() => {
    setSelectedLocationId();
  }, [currentFormData.address, setSelectedLocationId]);

  const joinButtonPasswordlessStyle = {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '16px',
    padding: '12px 22px',
    minWidth: 192,
    '&:disabled': {
      background: `${colors.gray['200']} !important`,
      border: 'none',
      color: '#fff !important',
    },
  };

  return (
    <Form id="signup" data-test-id="step-signup" onSubmit={handleSubmit}>
      {!isMobile && <DashlineSeparator />}
      <UsernameInputV2
        onFocus={() => {
          handleJoinVysionEvent({
            action: VYSION_ACTIONS.ON_FOCUS,
            event: JoinOnFocusVysionEventsEnum.FIRSTNAME,
          });
        }}
        inputLabel="First Name / Nickname"
        onChange={e => {
          setCurrentFormData({ username: e.target.value });
        }}
        value={currentFormData.username}
        customStyle={{
          marginRight: 0,
          fontSize: 15,
          fontWeight: '600 !important',
          paddingBottom: 0,
          maxWidth: isMobile ? '100%' : 452,
        }}
        customStyleLabel={{ fontWeight: 600 }}
        placeholder="First names exhibit authenticity"
        joinForm
        showErrorAtTop
      />
      <DashlineSeparator />
      <Autocomplete
        inputProps={{
          value: currentFormData.address || '',
          id: 'location',
          type: 'text',
          onChange: handleLocationChange,
          status: addressValidationStatus() || (invalidLocation && 'error') || 'default',
          statusMessage: addressValidationMsg() || (invalidLocation && 'You must select a location.'),
          inputLabel: 'Location',
          onBlur: handleLocationBlur,
        }}
        onSelect={handleSelectLocation}
        customStyle={{ marginRight: 0, fontSize: 15, paddingBottom: 0 }}
        customStyleLabel={{ fontWeight: 600 }}
        customInputStyle={{ borderColor: 'rgba(0, 0, 0, 0.15) !important' }}
        joinForm
        joinFormVariant
        isRequired={false}
      />

      <DashlineSeparator />

      {isJoinCaptchaEnabled && (
        <HCaptchaContainer>
          <Hcaptcha
            cfCountry={cf_country}
            storeCaptchaResponse={token => setCurrentFormData({ 'h-captcha-response': token })}
            resetCaptcha={isSubmitFailed}
          />
        </HCaptchaContainer>
      )}

      <ButtonContainer>
        <JoinActionButton
          label={isLoading ? 'Creating Account...' : 'Create Account'}
          type="submit"
          isDisabled={!allowSubmit || isLoading}
          customStyle={{ width: 194, ...(isPasswordless ? joinButtonPasswordlessStyle : {}) }}
          isDefault={true}
          buttonType="primaryRed"
        />
      </ButtonContainer>
      <BottomMsg>
        By clicking Create Account, you agree to the{' '}
        <TextLink
          data-test-id="label-sign_in"
          to={termsOfUseUrl(isEuUser)}
          style={{ color: '#8F8F8F', fontWeight: 400, fontSize: 14, textDecoration: 'underline' }}
          target="_blank"
        >
          Terms of Use
        </TextLink>{' '}
      </BottomMsg>
    </Form>
  );
};

export default JoinCreateAccountV2;
